import PersonRemoveOutlined from "@mui/icons-material/PersonRemoveOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getOptionLabelOfUserOrEmailMapping } from "../../../utils/type/type.utils";
import { UserOrEmailMapping } from "../typings/admin.types";

/** Scroll the assigned-people list once it is longer than the dialog (px). */
const ROSTER_MAX_HEIGHT_PX = 280;

/** Inset for the count and the people rows under the search field. */
const ROSTER_INLINE_PADDING_SX = { px: "4px", pt: "8px" };

type DepartmentUserRosterProps = {
  /** People already in the department, in surname order. */
  users: UserOrEmailMapping[];
  onRemove: (user: UserOrEmailMapping) => void;
};

type DepartmentUserRosterSectionProps = {
  /** Assigned people that match the current search, in surname order. */
  visibleUsers: UserOrEmailMapping[];
  /** True while the search field has a keyword, so the count reads as matches found. */
  searchActive: boolean;
  onRemove: (user: UserOrEmailMapping) => void;
};

/** Count, and the scrolling roster when the search matches someone. */
export function DepartmentUserRosterSection({
  visibleUsers,
  searchActive,
  onRemove
}: Readonly<DepartmentUserRosterSectionProps>) {
  const { t } = useTranslation();
  const count = visibleUsers.length;
  let countLabel = t("_departmentUsers_manyPeople", { count });
  if (searchActive && count === 1) {
    countLabel = t("_departmentUsers_onePersonFound");
  } else if (searchActive) {
    countLabel = t("_departmentUsers_manyPeopleFound", { count });
  } else if (count === 1) {
    countLabel = t("_departmentUsers_onePerson");
  }
  const roster =
    visibleUsers.length > 0 ? (
      <DepartmentUserRoster users={visibleUsers} onRemove={onRemove} />
    ) : null;
  return (
    <Box sx={ROSTER_INLINE_PADDING_SX}>
      <Typography data-testid="manage-roster-count" sx={{ pb: "4px" }}>
        {countLabel}
      </Typography>
      {roster}
    </Box>
  );
}

/**
 * Compact one-line rows for colleagues already in the department.
 * The list scrolls on its own so the search field above it stays put.
 */
export default function DepartmentUserRoster({
  users,
  onRemove
}: Readonly<DepartmentUserRosterProps>) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="manage-roster"
      sx={{ maxHeight: ROSTER_MAX_HEIGHT_PX, overflowY: "auto", overflowX: "hidden" }}
    >
      {users.map((user, index) => {
        const name = getOptionLabelOfUserOrEmailMapping(user);
        const email = user.email ?? "";
        const showEmail = email.length > 0 && email !== name;
        const rowKey = user.userId?.trim() || email || name;
        return (
          <Box
            key={rowKey}
            data-testid="manage-roster-row"
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 42%) minmax(0, 1fr) auto",
              alignItems: "center",
              columnGap: 1,
              minHeight: 32,
              py: 0.25,
              borderTop: index === 0 ? 0 : "1px solid",
              borderColor: "divider"
            }}
          >
            <Typography noWrap sx={{ minWidth: 0, fontSize: "0.8125rem", fontWeight: 500 }}>
              {name}
            </Typography>
            {showEmail ? (
              <Typography
                noWrap
                title={email}
                sx={{
                  minWidth: 0,
                  textAlign: "left",
                  fontSize: "0.75rem",
                  color: "text.secondary"
                }}
              >
                {email}
              </Typography>
            ) : (
              <Box />
            )}
            <Tooltip title={<Typography>{t("Remove")}</Typography>}>
              <IconButton
                size="small"
                aria-label={t("_departmentUsers_remove", { name })}
                data-testid="manage-roster-remove"
                onClick={() => onRemove(user)}
                sx={{ color: "text.secondary" }}
              >
                <PersonRemoveOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}
